// Country indicators at a date: latest value, share of the world, windowed counts.
//
// Year rule (ARCHITECTURE §3.5): a date uses the latest year ≤ year(D). Annual data cannot be split
// within a year, so a date early in a year already sees that year's full value.
//
// World totals (ARCHITECTURE §4 "shares of the world total"):
//   - 'WLD' in the series when present, at the same year as the country's value;
//   - otherwise the sum over countries.json states of each state's latest value ≤ the year.
//     Series also carry territories (HKG, PRI, …) that are not in countries.json; they are left
//     out of the sum so that no area is counted twice.
//
// One year per share (ARCHITECTURE §1, "no mixed years"): a level's shares (`shareAt`) are taken in
// the common year, the latest year ≤ D in which enough states report (`MIN_COVERAGE` of the most
// that ever did by D). A state without a value in it keeps its own newest value, divided by the total
// of that value's year and labelled with that year, so it greys as stale. A summed total needs an
// adequately covered year; a world series (WLD) does not.
//
// Sources follow the datum (`sourcesFor`): meta[id].source_groups, the first group whose `countries`
// and `years` both admit it (a group with neither is the default), else meta[id].sources.

import type { Indicators, SourceId } from '../core/schema.ts';
import { latestAt, valueIn } from './util.ts';

export const WORLD = 'WLD';

export interface Share {
  share: number;
  value: number;
  year: number;
}

/** Share over a window of years (e.g. the last two Games). */
export interface WindowShare {
  share: number;
  value: number;
  years: number[];
}

export interface IndicatorIndex {
  ind: Indicators;
  /** States counted in a summed world total. */
  states: Set<string>;
  /** Summed world totals by 'id|year' (every state's share needs the same one). */
  sums: Map<string, number>;
  /** Per indicator, ascending [year, states with a value that year]. */
  coverage: Map<string, Array<[number, number]>>;
  /** Sources of a world total by 'id|year'. */
  totalSources: Map<string, SourceId[]>;
}

export function indicatorIndex(ind: Indicators, states: Iterable<string>): IndicatorIndex {
  return { ind, states: new Set(states), sums: new Map(), coverage: new Map(), totalSources: new Map() };
}

/** A year counts as covered when this share of the most states ever reporting (by D) report in it. */
const MIN_COVERAGE = 2 / 3;

/** meta[id].source_groups as baked (indicators.json); the shared schema does not list it yet. */
interface SourceGroup {
  sources: SourceId[];
  countries?: string[];
  years?: number[];
}

export function series(ix: IndicatorIndex, id: string, iso3: string): Array<[number, number]> | undefined {
  return ix.ind.series[id]?.[iso3];
}

export function sourcesOf(ix: IndicatorIndex, id: string): SourceId[] {
  return ix.ind.meta[id]?.sources ?? [];
}

/** Sources of one datum: the first source group admitting its country and year, else all the indicator's. */
export function sourcesFor(ix: IndicatorIndex, id: string, iso3: string, year: number): SourceId[] {
  const meta: (Indicators['meta'][string] & { source_groups?: SourceGroup[] }) | undefined = ix.ind.meta[id];
  const group = meta?.source_groups?.find((g) => (!g.countries || g.countries.includes(iso3)) && (!g.years || g.years.includes(year)));
  return group ? group.sources : sourcesOf(ix, id);
}

export function unitOf(ix: IndicatorIndex, id: string): string {
  return ix.ind.meta[id]?.unit ?? '';
}

/** Latest [year, value] ≤ year for a country. */
export function latest(ix: IndicatorIndex, id: string, iso3: string, year: number): [number, number] | null {
  return latestAt(series(ix, id, iso3), year);
}

/** Σ over states of each state's latest value ≤ year (memoised). */
function summed(ix: IndicatorIndex, id: string, year: number): number {
  const key = `${id}|${year}`;
  const hit = ix.sums.get(key);
  if (hit !== undefined) {
    return hit;
  }
  const all = ix.ind.series[id] ?? {};
  let sum = 0;
  for (const iso3 of ix.states) {
    const v = latestAt(all[iso3], year);
    sum += v ? v[1] : 0;
  }
  ix.sums.set(key, sum);
  return sum;
}

/** World total at `year` (see header). */
export function worldTotal(ix: IndicatorIndex, id: string, year: number): number {
  const wld = valueIn(series(ix, id, WORLD), year);
  return wld ?? summed(ix, id, year);
}

export function hasWorld(ix: IndicatorIndex, id: string): boolean {
  return (series(ix, id, WORLD)?.length ?? 0) > 0;
}

/** How many states have a value in each year (ascending). */
function coverageOf(ix: IndicatorIndex, id: string): Array<[number, number]> {
  const hit = ix.coverage.get(id);
  if (hit) {
    return hit;
  }

  const counts = new Map<number, number>();
  for (const iso3 of ix.states) {
    for (const [y] of series(ix, id, iso3) ?? []) {
      counts.set(y, (counts.get(y) ?? 0) + 1);
    }
  }
  const list = [...counts].sort((a, b) => a[0] - b[0]);
  ix.coverage.set(id, list);
  return list;
}

/** Years ≤ `year` in which enough states report to stand for the world (newest last). */
function coveredYears(ix: IndicatorIndex, id: string, year: number): number[] {
  const list = coverageOf(ix, id).filter(([y]) => y <= year);
  const most = Math.max(0, ...list.map(([, n]) => n));
  return list.filter(([, n]) => n >= MIN_COVERAGE * most).map(([y]) => y);
}

/** The year a level's shares are taken in at `year` (see the header), or null when no year is covered. */
export function commonYear(ix: IndicatorIndex, id: string, year: number): number | null {
  return coveredYears(ix, id, year).pop() ?? null;
}

/** Σ over states of the values in exactly `year`. */
function summedIn(ix: IndicatorIndex, id: string, year: number): number {
  const key = `${id}|in|${year}`;
  const hit = ix.sums.get(key);
  if (hit !== undefined) {
    return hit;
  }
  let sum = 0;
  for (const iso3 of ix.states) {
    sum += valueIn(series(ix, id, iso3), year) ?? 0;
  }
  ix.sums.set(key, sum);
  return sum;
}

/** The total a value of `dataYear` is shared out of: WLD's, else the states' when `dataYear` is covered at `year`. */
function totalFor(ix: IndicatorIndex, id: string, dataYear: number, year: number): number {
  const wld = valueIn(series(ix, id, WORLD), dataYear);
  if (wld !== null) {
    return wld;
  }
  return coveredYears(ix, id, year).includes(dataYear) ? summedIn(ix, id, dataYear) : 0;
}

/** Who says a total: WLD's datum when there is one, else the datum of every state that adds to it. */
export function totalSourcesOf(ix: IndicatorIndex, id: string, dataYear: number): SourceId[] {
  const key = `${id}|${dataYear}`;
  const hit = ix.totalSources.get(key);
  if (hit) {
    return hit;
  }

  const world = valueIn(series(ix, id, WORLD), dataYear) !== null;
  const who = world ? [WORLD] : [...ix.states].filter((iso3) => valueIn(series(ix, id, iso3), dataYear) !== null);
  const out = [...new Set(who.flatMap((iso3) => sourcesFor(ix, id, iso3, dataYear)))];
  ix.totalSources.set(key, out);
  return out;
}

/**
 * A state's share of the world: its value in the common year at `year`, or its newest older one
 * (stale, with its own year) divided by the total of that year. Null without a value or a total.
 */
export function shareAt(ix: IndicatorIndex, id: string, iso3: string, year: number): Share | null {
  const common = commonYear(ix, id, year);
  const hit = common === null ? null : latest(ix, id, iso3, common);
  if (!hit) {
    return null;
  }

  const total = totalFor(ix, id, hit[0], year);
  if (!(total > 0)) {
    return null;
  }
  return { share: hit[1] / total, value: hit[1], year: hit[0] };
}

/** The value of `iso3` in exactly the common year at `year`, with its year. Null when it has none there. */
export function commonValue(ix: IndicatorIndex, id: string, iso3: string, year: number): [number, number] | null {
  const common = commonYear(ix, id, year);
  const v = common === null ? null : valueIn(series(ix, id, iso3), common);
  return common === null || v === null ? null : [common, v];
}

/** Largest value over states in exactly the common year at `year`. */
export function commonMax(ix: IndicatorIndex, id: string, year: number): number {
  const common = commonYear(ix, id, year);
  let best = 0;
  for (const iso3 of ix.states) {
    const v = common === null ? null : valueIn(series(ix, id, iso3), common);
    best = v !== null && v > best ? v : best;
  }
  return best;
}

/** The `n` latest years ≤ `year` that have a world value (WLD, or any state when there is none). */
export function windowYears(ix: IndicatorIndex, id: string, year: number, n: number): number[] {
  const years = new Set<number>();
  const wld = series(ix, id, WORLD);
  const lists = wld ? [wld] : Object.values(ix.ind.series[id] ?? {});
  for (const s of lists) {
    for (const [y] of s) {
      if (y <= year) {
        years.add(y);
      }
    }
  }
  return [...years].sort((a, b) => b - a).slice(0, n).sort((a, b) => a - b);
}

/**
 * Share of a count over a window of years. A state missing in a year counts 0 there (count data:
 * Epoch lists only states with models, a medal table only medal winners).
 */
export function windowShare(ix: IndicatorIndex, id: string, iso3: string, year: number, n: number): WindowShare | null {
  const years = windowYears(ix, id, year, n);
  if (years.length === 0) {
    return null;
  }
  let value = 0;
  let total = 0;
  for (const y of years) {
    value += valueIn(series(ix, id, iso3), y) ?? 0;
    total += worldTotal(ix, id, y);
  }
  if (!(total > 0)) {
    return null;
  }
  return { share: value / total, value, years };
}

/** Largest latest-≤-year value over states, with its year. */
export function maxAt(ix: IndicatorIndex, id: string, year: number): number {
  const all = ix.ind.series[id] ?? {};
  let best = 0;
  for (const iso3 of ix.states) {
    const hit = latestAt(all[iso3], year);
    if (hit && hit[1] > best) {
      best = hit[1];
    }
  }
  return best;
}

/** Min and max over every state and year (fixed lens scales, comparable across dates). */
export function rangeOf(ix: IndicatorIndex, id: string, values: 'positive' | 'any'): [number, number] | null {
  const all = ix.ind.series[id] ?? {};
  let lo = Infinity;
  let hi = -Infinity;
  for (const iso3 of ix.states) {
    for (const [, v] of all[iso3] ?? []) {
      if (values === 'positive' && !(v > 0)) {
        continue;
      }
      lo = Math.min(lo, v);
      hi = Math.max(hi, v);
    }
  }
  return lo <= hi ? [lo, hi] : null;
}
